import React, { useState, useEffect } from 'react';

export default function VolumePricingManager({ productId, productName, onUpdate }) {
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [minQuantity, setMinQuantity] = useState('');
  const [price, setPrice] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchTiers();
  }, [productId]);

  const fetchTiers = async () => {
    setLoading(true);
    try {
      const res = await fetch(`/api/volume-pricing/product/${productId}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await res.json();
      setTiers(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Failed to fetch tiers:', error);
    } finally {
      setLoading(false);
    }
  };

  const addTier = async () => {
    if (!minQuantity || !price) return;
    setSaving(true);
    try {
      const res = await fetch(`/api/volume-pricing/product/${productId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          minQuantity: parseInt(minQuantity),
          price: parseFloat(price)
        })
      });
      if (res.ok) {
        await fetchTiers();
        setShowAdd(false);
        setMinQuantity('');
        setPrice('');
        if (onUpdate) onUpdate();
      } else {
        const error = await res.json();
        alert(error.error || 'Failed to add tier');
      }
    } catch (error) {
      alert('Error adding tier');
    } finally {
      setSaving(false);
    }
  };

  const deleteTier = async (tierId) => {
    if (!window.confirm('Delete this volume tier?')) return;
    try {
      const res = await fetch(`/api/volume-pricing/product/${productId}/tier/${tierId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (res.ok) {
        await fetchTiers();
        if (onUpdate) onUpdate();
      }
    } catch (error) {
      alert('Error deleting tier');
    }
  };

  if (loading) return <div className="text-xs text-gray-400">Loading tiers...</div>;

  return (
    <div className="mt-2">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-gray-600">Volume Pricing</span>
        <button
          onClick={() => setShowAdd(!showAdd)}
          className="text-xs text-blue-600 hover:text-blue-800"
        >
          {showAdd ? 'Cancel' : '+ Add tier'}
        </button>
      </div>

      {/* Tiers list */}
      {tiers.length > 0 && (
        <div className="mt-1 space-y-1">
          {tiers.map(tier => (
            <div key={tier._id} className="flex items-center justify-between text-xs bg-gray-50 rounded px-2 py-1">
              <span>
                <span className="font-medium">{tier.minQuantity}+</span>
                <span className="text-gray-500"> @</span>
                <span className="font-medium text-green-600">${tier.price}</span>
              </span>
              <button
                onClick={() => deleteTier(tier._id)}
                className="text-red-500 hover:text-red-700 text-xs"
              >
                ✕
              </button>
            </div>
          ))}
        </div>
      )}

      {tiers.length === 0 && !showAdd && (
        <p className="text-xs text-gray-400 mt-1">No volume tiers set</p>
      )}

      {/* Add tier form */}
      {showAdd && (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <input
            type="number"
            min="1"
            placeholder="Min qty"
            value={minQuantity}
            onChange={(e) => setMinQuantity(e.target.value)}
            className="w-16 border rounded px-2 py-1 text-xs"
          />
          <input
            type="number"
            min="0"
            step="0.01"
            placeholder="Price"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            className="w-20 border rounded px-2 py-1 text-xs"
          />
          <button
            onClick={addTier}
            disabled={saving || !minQuantity || !price}
            className="bg-green-600 text-white px-2 py-1 rounded text-xs hover:bg-green-700 disabled:bg-gray-400"
          >
            Add
          </button>
        </div>
      )}
    </div>
  );
}
